import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@/layouts/AppLayout';
import { SettingsLayout } from '@/layouts/SettingsLayout';
import { PlatformRouteGuard } from '@/pages/platform/PlatformRouteGuard';
import { DashboardPage } from '@/pages/DashboardPage';
import { PlaceholderPage } from '@/pages/PlaceholderPage';
import { TeamStatsPage } from '@/pages/TeamStatsPage';
import { EmployeeDetailPage } from '@/pages/EmployeeDetailPage';
import { EmployeeReviewPage } from '@/pages/EmployeeReviewPage';
import { EmployeeClientDetailPage } from '@/pages/EmployeeClientDetailPage';
import { EmployeeClientProjectsPage } from '@/pages/EmployeeClientProjectsPage';
import { DomainAnalyticsPage } from '@/pages/DomainAnalyticsPage';
import { ReportsMonthlyPage } from '@/pages/ReportsMonthlyPage';
import { ApplicationsReportPage } from '@/pages/ApplicationsReportPage';
import { GroupedReportPage } from '@/pages/GroupedReportPage';
import { ActivityLogPage } from '@/pages/ActivityLogPage';
import { ActivityLogEventsPage } from '@/pages/ActivityLogEventsPage';
import { WorkLogReviewPage } from '@/pages/WorkLogReviewPage';
import { TimesheetRangePage } from '@/pages/TimesheetRangePage';
import { TimesheetDailyPage } from '@/pages/TimesheetDailyPage';
import { CategorizeTimePage } from '@/pages/CategorizeTimePage';
import { ManageClientsPage } from '@/pages/ManageClientsPage';
import { ManageProjectsPage } from '@/pages/ManageProjectsPage';
import { ManageRulesPage } from '@/pages/ManageRulesPage';
import { ViewRulePage } from '@/pages/ViewRulePage';
import { SettingsOrgPage } from '@/pages/SettingsOrgPage';
import { SettingsTeamPage } from '@/pages/SettingsTeamPage';
import { SettingsAccessPage } from '@/pages/SettingsAccessPage';
import { SettingsCollectorsPage } from '@/pages/SettingsCollectorsPage';
import { SettingsJiraPage } from '@/pages/SettingsJiraPage';
import { UserSettingsPage } from '@/pages/UserSettingsPage';
import { ClientDetailPage } from '@/pages/ClientDetailPage';
import { ProjectDetailPage } from '@/pages/ProjectDetailPage';
import { InviteAcceptPage } from '@/pages/InviteAcceptPage';
import { NoOrgsPage } from '@/pages/NoOrgsPage';

export const router = createBrowserRouter([
  // Landing pages — rendered OUTSIDE AppLayout (no sidebar / topbar).
  // These routes are reachable without an active organization, so they
  // must not depend on the app shell or any tenant-scoped data.
  {
    path: '/invites/accept',
    element: <InviteAcceptPage />,
  },
  {
    path: '/no-organizations',
    element: <NoOrgsPage />,
  },
  {
    path: '/',
    element: <AppLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: <DashboardPage />,
      },
      {
        path: 'team',
        element: <TeamStatsPage />,
      },
      {
        path: 'timesheet/range',
        element: <TimesheetRangePage />,
      },
      {
        path: 'timesheet/daily',
        element: <TimesheetDailyPage />,
      },
      {
        path: 'analytics/domains',
        element: <DomainAnalyticsPage />,
      },
      {
        path: 'clients',
        element: <ManageClientsPage />,
      },
      {
        path: 'clients/new',
        element: <ManageClientsPage />,
      },
      {
        path: 'clients/:clientId/edit',
        element: <ManageClientsPage />,
      },
      {
        // Renders ManageClientsPage with the Add-Project SlidePanel open,
        // pre-populated with the URL's :clientId. The page decides via
        // useMatch — no dedicated element.
        path: 'clients/:clientId/projects/new',
        element: <ManageClientsPage />,
      },
      {
        path: 'clients/:clientId',
        element: <ClientDetailPage />,
      },
      {
        path: 'projects',
        element: <ManageProjectsPage />,
      },
      {
        path: 'projects/:projectId/edit',
        element: <ManageProjectsPage />,
      },
      {
        path: 'projects/detail',
        element: <ProjectDetailPage />,
      },
      {
        path: 'rules',
        element: <ManageRulesPage />,
      },
      {
        path: 'rules/:ruleId',
        element: <ViewRulePage />,
      },
      {
        path: 'categorize',
        element: <CategorizeTimePage />,
      },
      {
        path: 'reports/monthly',
        element: <ReportsMonthlyPage />,
      },
      {
        path: 'reports/applications',
        element: <ApplicationsReportPage />,
      },
      {
        path: 'reports/grouped',
        element: <GroupedReportPage />,
      },
      {
        path: 'reports/activity-log',
        element: <ActivityLogPage />,
      },
      {
        path: 'reports/activity-log/events',
        element: <ActivityLogEventsPage />,
      },
      {
        // Task 159c — admin/owner-only Work Log Review page. The page
        // component bounces non-admins to /dashboard; the route stays
        // registered for everyone so a stale bookmark gets a redirect
        // rather than a 404.
        path: 'reports/work-log-review',
        element: <WorkLogReviewPage />,
      },
      {
        path: 'settings',
        element: <SettingsLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/settings/organization" replace />,
          },
          {
            path: 'organization',
            element: <SettingsOrgPage />,
          },
          {
            path: 'team',
            element: <SettingsTeamPage />,
          },
          {
            path: 'access',
            element: <SettingsAccessPage />,
          },
          {
            path: 'collectors',
            element: <SettingsCollectorsPage />,
          },
          {
            path: 'integrations/jira',
            element: <SettingsJiraPage />,
          },
          {
            path: 'account',
            element: <UserSettingsPage />,
          },
        ],
      },
      // Task 130 D5/D6 — platform-admin tenant list. Non-admins are
      // redirected to `/` by the guard; the lazy chunk never loads for
      // them.
      {
        path: 'platform/tenants',
        element: <PlatformRouteGuard page="tenants" />,
      },
      // Task 158d — host-categorization platform-admin pages.
      {
        path: 'platform/hosts',
        element: <PlatformRouteGuard page="hosts" />,
      },
      {
        path: 'platform/hosts/:host',
        element: <PlatformRouteGuard page="host-detail" />,
      },
      {
        path: 'platform/categories',
        element: <PlatformRouteGuard page="categories" />,
      },
      // 158e — taxonomy editor + promotion-status pages.
      {
        path: 'platform/categories/edit',
        element: <PlatformRouteGuard page="categories-edit" />,
      },
      {
        path: 'platform/categories/promotion-status',
        element: <PlatformRouteGuard page="promotion-status" />,
      },
      // Legacy routes preserved for backward compat
      {
        path: 'timesheet',
        element: <PlaceholderPage title="Timesheet Daily" taskLabel="Coming in task 060" />,
      },
      {
        path: 'employees',
        element: <PlaceholderPage title="Employees" taskLabel="Coming in task 060" />,
      },
      {
        path: 'employees/:employeeId',
        element: <EmployeeDetailPage />,
      },
      {
        path: 'employees/:employeeId/review',
        element: <EmployeeReviewPage />,
      },
      {
        path: 'employees/:employeeId/clients/:clientId',
        element: <EmployeeClientDetailPage />,
      },
      {
        path: 'employees/:employeeId/clients/:clientId/projects',
        element: <EmployeeClientProjectsPage />,
      },
      {
        path: 'employees/:employeeId/timesheet',
        element: <PlaceholderPage title="Timesheet" taskLabel="Coming in task 032" />,
      },
      {
        path: 'reports',
        element: <Navigate to="/reports/monthly" replace />,
      },
    ],
  },
]);
